import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { BROKEN_IMAGE_PLACEHOLDER } from './brokenImage.js';
/** Le délai avant de retenter : assez court pour que l'acheteuse ne voie rien, assez long pour laisser
 *  passer le 503 d'une rafale. */
const RETRY_DELAY_MS = 600;
/** Les étapes de l'échelle. `OK` = premier rendu, `DONE` = on n'essaie plus rien. Un objet figé plutôt
 *  qu'un `const enum`, que le bundler ne sait pas effacer sous `isolatedModules`. */
const Stage = { OK: 0, RETRY: 1, ORIGINAL: 2, DONE: 3 };
/** La même URL, mais que le navigateur ne peut pas confondre avec celle qu'il vient de rater. */
function bust(url) {
    return `${url}${url.includes('?') ? '&' : '?'}aqxr=1`;
}
export function ResilientImg({ src, srcSet, sizes, originalSrc, avifSrcSet, onError, ...rest }) {
    const [stage, setStage] = useState(Stage.OK);
    const ref = useRef(null);
    const timer = useRef(null);
    // Une étape à la fois, et on saute le repli « original » quand il n'y en a pas (image étrangère) ou
    // quand il est déjà ce qu'on affiche (aucune variante en jeu) : sans ce saut on retenterait la même
    // URL deux fois de suite et le défaut durerait 600 ms de plus pour rien.
    const nextStage = (from) => {
        if (from >= Stage.ORIGINAL)
            return Stage.DONE;
        if (from === Stage.RETRY && (!originalSrc || originalSrc === src))
            return Stage.DONE;
        return (from + 1);
    };
    useEffect(() => () => {
        if (timer.current !== null)
            clearTimeout(timer.current);
    }, []);
    // UNE NOUVELLE PHOTO REPART DE ZÉRO. Depuis que la galerie remploie ses `<img>` d'une couleur à
    // l'autre (clé par position, pour que l'ancienne photo tienne l'écran jusqu'à la nouvelle), un seul
    // hoquet sur une photo condamnait toutes les suivantes : l'échelle restait en position « repli », donc
    // la couleur d'après était demandée avec `?aqxr=1` et SANS `srcset` — l'original de 2560px à la place
    // du palier, pour une photo qui n'avait jamais raté. L'état décrit CETTE image, pas l'élément.
    // Ajusté PENDANT le rendu (le motif « dériver un état d'une prop » sanctionné par React) et non dans
    // un effet : un effet peindrait une image de plus avec l'ANCIENNE étape, donc une requête `?aqxr=1`
    // inutile pour une photo qui n'a jamais raté.
    const [lastSrc, setLastSrc] = useState(src);
    if (lastSrc !== src) {
        setLastSrc(src);
        setStage(Stage.OK);
        if (timer.current !== null) {
            clearTimeout(timer.current);
            timer.current = null;
        }
    }
    const fail = () => {
        if (timer.current !== null)
            return; // une tentative déjà en vol
        timer.current = setTimeout(() => {
            timer.current = null;
            setStage((s) => nextStage(s));
        }, RETRY_DELAY_MS);
    };
    // L'échec qu'on n'a pas entendu : l'image a été demandée avant l'hydratation, l'événement `error` est
    // parti sans écouteur. `complete` + `naturalWidth === 0` est la trace qu'il laisse derrière lui.
    useEffect(() => {
        const el = ref.current;
        if (!el || !el.getAttribute('src'))
            return;
        if (el.complete && el.naturalWidth === 0)
            fail();
        // Relancé à chaque étape : le repli peut échouer à son tour, et lui aussi peut arriver « déjà raté »
        // si le navigateur avait la réponse en cache.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stage]);
    // `srcSet` ne survit pas à un repli : il l'emporterait sur le `src` de secours et le navigateur
    // redemanderait la variante qui vient de rater.
    const degraded = stage !== Stage.OK;
    // Abandonner, c'est ARRÊTER D'ESSAYER — pas changer d'URL une fois de plus. Sans cette ligne, le
    // dernier échec renvoyait l'affichage sur `originalSrc`, donc sur une 3e requête (la même que la 2e
    // quand il n'y a pas de variante en jeu) : une image de plus demandée pour rien, et une boîte qui
    // clignote sous les yeux de l'acheteuse. On reste sur la dernière URL réellement tentée.
    const shownStage = stage === Stage.DONE
        ? originalSrc && originalSrc !== src
            ? Stage.ORIGINAL
            : Stage.RETRY
        : stage;
    // ABANDONNER, C'EST AUSSI NE PAS LAISSER LE CARRÉ BARRÉ (règle de la fondatrice, 23/09/2026) : une
    // image qui a épuisé son échelle est remplacée par le cadre neutre partagé avec le moteur vanille.
    const shown = stage === Stage.DONE
        ? BROKEN_IMAGE_PLACEHOLDER
        : shownStage === Stage.OK
            ? src
            : shownStage === Stage.RETRY
                ? bust(src)
                : (originalSrc ?? bust(src));
    const image = (_jsx("img", { ...rest, "data-aqx-img-react": "", "data-aqx-img-broken": stage === Stage.DONE ? 'true' : undefined, ref: ref, src: shown, srcSet: degraded ? undefined : srcSet, sizes: degraded ? undefined : sizes, onError: (e) => {
            onError?.(e);
            if (stage !== Stage.DONE)
                fail();
        } }));
    if (!avifSrcSet || degraded)
        return image;
    return (_jsxs("picture", { style: { display: 'contents' }, children: [_jsx("source", { type: "image/avif", srcSet: avifSrcSet, sizes: sizes }), image] }));
}
//# sourceMappingURL=ResilientImg.js.map